import {EventEmitter, Injectable, OnDestroy} from '@angular/core';
import {MmFilterService} from '../mm-filter/mm-filter.service';
import {BehaviorSubject, firstValueFrom, from, iif, Observable, of, Subject} from 'rxjs';
import {Fault, LayoutFault, ViewFault} from '@docma-mm-core/fault/fault';
import {MmFilterModel} from '../mm-filter/mm-filter.model';
import {Project} from '@docma-mm-core/project/project';
import {NotificationService} from '@mydocma/component-library/src/lib/notification';
import {Structure} from '@docma-mm-core/structure/structure';
import {ConnectivityService} from '@mydocma/core';
import {filter, finalize, first, map, switchMap, takeUntil, tap} from 'rxjs/operators';
import {FaultLayoutApiService} from '@docma-mm-core/fault/fault-layout.api.service';
import {Attachment, AttachmentSource} from '@docma-mm-core/attachment/attachment';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {FaultDataSearch, FaultDataSearchFilter} from '@docma-mm-core/fault/fault-data';
import {LoadState} from '@docma-mm-core/model/load-state/load-state';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {AttachmentService} from '@docma-mm-core/attachment/attachment.service';
import {ChangeStatusEnum} from '@docma-mm-core/model/change-state/change-status.enum';
import {SyncStatusEnum} from '@docma-mm-core/model/sync-state/sync-status.enum';
import {AttachmentLoadingTypeEnum} from '@docma-mm-core/attachment/attachment-loading-type.enum';
import {SelectionService} from '@mydocma/component-library/src/lib/selection';
import {FilterCriteria} from '@docma-mm-core/filter/filter-criteria';
import {LayoutStateModel} from '../mm-filter/layout/layout-state.model';
import {MmViewService} from '../mm-view.service';
import {MmLayoutStateService} from '../mm-filter/layout/layout-state.service';
import {isIdsEquals} from '@docma-mm-core/utils/utils';
import {FaultSortingSourceEnum} from '@docma-mm-core/fault/fault-sorting-source.enum';
import {MmProjectLoaderService} from '../mm-project/mm-project-loader.service';
import {AppContainerService, PendingService, PersonalizationService} from '@mydocma/app-container';
import {ActivityService} from '@docma-mm-core/activity/activity.service';
import {MmFaultsReportingService} from '../mm-reporting/faults/mm-faults-reporting.service';
import {MmReportTypeEnum} from '../mm-reporting/mm-report-type.enum';
import {MmFaultsCountService} from './mm-faults-view/services/mm-faults-count.service';
import {FaultSyncService, SyncStateChange} from '@docma-mm-core/fault/sync/fault-sync.service';
import {GroupSyncService} from '@docma-mm-core/group/group-sync.service';
import {MultiActivity} from '@docma-mm-core/activity/multi-activity';
import {Activity, ActivityData} from '@docma-mm-core/activity/activity';
import {ActivityPatchModeEnum} from '@docma-mm-core/activity/activity-patch-mode.enum';
import {CreatedOnFieldFormat, ModifiedOnFieldFormat} from '@docma-mm-core/fault/field-formats';
import {User} from '@docma-mm-core/user/user';
import {MmPinsLoaderService} from './mm-pins-loader.service';
import {BpPin} from '../bp-common/models/bp-pin';
import {ColDefsService} from './mm-faults-ag-grid/col-defs/col-defs.service';
import {PersonalizationKeysEnum} from '@docma-mm-core/personalization/personalization-keys.enum';
import {HeaderToggleMenuEnum} from './mm-faults-view/services/mm-faults-split.service';
import {MmProjectService} from '../mm-project/mm-project.service';
import {LoadLayoutFaultsOptions} from './load-layout-faults-options.enum';
import {MmTranslateService} from '../mm-translate.service';

@UntilDestroy()
@Injectable()
export class MmFaultsLoaderService implements OnDestroy {

  private filter: MmFilterModel;
  private faultsSubject = new BehaviorSubject<{loadedFaults: ViewFault[], loadOptions: LoadLayoutFaultsOptions}>({loadedFaults: [], loadOptions: LoadLayoutFaultsOptions.None});
  private loadingSubject = new BehaviorSubject<boolean>(false);
  private layoutFaultSubject = new Subject<ViewFault>();
  private layoutFaultsSubject = new Subject<ViewFault[]>();

  private loadLayoutFaultsOptionsSubject = new BehaviorSubject<LoadLayoutFaultsOptions>(LoadLayoutFaultsOptions.None);
  public loadLayoutFaultsOptions$ = this.loadLayoutFaultsOptionsSubject.asObservable();

  private onLayoutStateChanged = new Subject<boolean>();
  private projectStructures: Structure[];
  private offline = false;
  private forceLayoutReload = false;
  private loadedFaults: ViewFault[] = []; // update
  private project: Project;
  private layoutState: LayoutStateModel;

  private loadingLayoutFaultsSubject = new BehaviorSubject<boolean>(false);
  private loadingFaultsSubject = new BehaviorSubject<boolean>(false);

  loadingLayoutFaults$ = this.loadingLayoutFaultsSubject.asObservable();

  public faults = this.faultsSubject.asObservable();

  public onFaultAdded = new EventEmitter<ViewFault>();
  public onFaultsRemoved = new EventEmitter<ViewFault[]>();

  public get faultsCount() {
    return (this.faultsSubject.value?.loadedFaults?.length ?? 0);
  }

  public get allFaultsCount() {
    return (this.faultsSubject.value?.loadedFaults?.length ?? 0);
  }

  public loadingState = this.loadingSubject.asObservable();
  public onFaultLoaded = this.faultService.onFaultLoaded;
  public onLayoutFaultLoaded = this.layoutFaultSubject.asObservable();
  public onLayoutFaultsLoaded = this.layoutFaultsSubject.asObservable();

  activitiesDeletedSubject = new Subject<Activity[]>();
  public onActivitiesDeleted = this.activitiesDeletedSubject.asObservable();

  private faultChangedSubject = new Subject<Fault>();
  onFaultChanged = this.faultChangedSubject.asObservable();

  private attachmentDeletedSubject = new Subject<{attachment: Attachment, fault: Fault, activity: Activity, modifiedOn: Date, modifier: User}>();
  onAttachmentDeleted =  this.attachmentDeletedSubject.asObservable();

  private syncStateChanges: { [faultUid: string]: SyncStateChange}[];
  private _currentFault: ViewFault;
  public get currentFault() {
    return this._currentFault;
  }
  public set currentFault(fault: ViewFault) {
    this._currentFault = fault;
  }

  public toggledLeftViews = [];

  private faultsLoadedGridNoFieldsCase: boolean = false;


  constructor(
    private readonly filterService: MmFilterService,
    private readonly attachmentService: AttachmentService,
    private readonly layoutStateService: MmLayoutStateService,
    private readonly faultService: FaultService,
    private readonly faultDataService: FaultLayoutApiService,
    private readonly connectivityService: ConnectivityService,
    private readonly notificationService: NotificationService,
    private readonly viewService: MmViewService,
    private readonly translateService: MmTranslateService,
    private readonly pendingService: PendingService,
    private readonly selectionService: SelectionService<ViewFault>,
    private readonly projectLoaderService: MmProjectLoaderService,
    private readonly mmFaultsCountService: MmFaultsCountService,
    private readonly mmFaultsReportingService: MmFaultsReportingService,
    private readonly activityService: ActivityService,
    private readonly groupSyncService: GroupSyncService,
    private readonly faultSyncService: FaultSyncService,
    private readonly pinsLoaderService: MmPinsLoaderService,
    private readonly colDefsService: ColDefsService,
    private readonly personalizationService: PersonalizationService,
    private readonly appContainerService: AppContainerService,
    private readonly mmProjectService: MmProjectService) {

    this.pendingService.registerPendingState(this.loadingFaultsSubject);
    this.pendingService.registerPendingState(this.loadingLayoutFaultsSubject);

    /* IMMEDIATE UNLOAD DATA OF PREVIOUS PROJECT */
    this.mmProjectService.loadingState
      .pipe(untilDestroyed(this), filter(Boolean))
      .subscribe(() => {
        this.onProjectChanged();
      });

    this.connectivityService.appIsInOfflineMode
      .pipe(
        untilDestroyed(this),
        tap(o => this.onOfflineChanged(o)),
        switchMap(o => this.viewService.currentProject$.pipe(filter(p => this.onCurrentProjectChanged(p)))),
        switchMap(p=> this.pinsLoaderService.loadPins(p)),
        switchMap(p => this.filterService.state),
        switchMap(fm => this.layoutStateService.currentLayoutState$.pipe(map(ls => ({ls, fm})))),
        switchMap(f => this.loadingSubject.pipe(filter(l => !l && this.shouldReloadFaults(f.fm, f.ls.state)), map(l => f)))
      )
      .subscribe(async f => {
        const loadOptions = f.ls.loadOptions;
        f.ls.loadOptions = LoadLayoutFaultsOptions.None;
        await this.reloadAllFaults(f.fm, f.ls.state, false, false, loadOptions);
      });

    this.faultSyncService.syncFaultsState$
      .pipe(untilDestroyed(this))
      .subscribe(params => {
        this.syncStateChanges = [];
        for (const syncStateChange of params.changes) {
          this.syncStateChanges[syncStateChange.faultUid] = syncStateChange;
        }
      });

    this.faultService.onFaultChanged
      .pipe(untilDestroyed(this))
      .subscribe(async f => this.faultChanged(f));

    this.faultService.onFaultDeleted
      .pipe(untilDestroyed(this))
      .subscribe(async f => this.removeFaults([f]));

    this.activityService.onActivityDeleted
      .pipe(untilDestroyed(this))
      .subscribe(async args => {
        this.removeGroupFaultActivites(args.activity, args.groupDelete, args.modifiedOn, args.modifier);
      });

    this.attachmentService.onAttachmentsAdded
      .pipe(untilDestroyed(this))
      .subscribe(args => {
        this.AddActivityAttachments(args.activityAttachments, args.activity);
      });

    this.attachmentService.onAttachmentChanged
      .pipe(untilDestroyed(this))
      .subscribe(args => {
        this.ChangeActivityAttachment(args.attachment, args.activity)
      });

    this.attachmentService.onAttachmentDeleted
      .pipe(untilDestroyed(this))
      .subscribe(args => {
        this.DeleteActivityAttachment(args.attachment, args.activity, args.modifiedOn, args.modifier);
        this.attachmentDeletedSubject.next(args);
      });


    this.mmFaultsReportingService.onReportReady
      .pipe(
        untilDestroyed(this),
        filter(r => r.reportType === MmReportTypeEnum.writing && r.projectId === this.project?.MyDocmaUid)
      )
      .subscribe(async reportTask => {
        if (reportTask.faults && reportTask.faults.length > 0) {
          const faultsToReload = reportTask.faults.map(fd => this.loadedFaults.find(lf => lf.uid === fd.UniqueId));
          if (faultsToReload.length > 0) {
            (await this.loadLayoutFaults(faultsToReload.filter(vf => vf.layoutState?.loadState?.status !== LoadStatusEnum.NotLoaded), true)).subscribe();
            await this.loadFaults(faultsToReload.filter(vf => vf.loadState?.status !== LoadStatusEnum.NotLoaded), true).toPromise();
          }
        }
      });

    this.groupSyncService.multiActivitySynced$
      .pipe(untilDestroyed(this))
      .subscribe(multiActivityData => {
        if (multiActivityData.FaultActivities) {
          multiActivityData.FaultActivities.map(async fa => {
            const fault = this.loadedFaults.find(f => f.uid === fa.Fault.UniqueId);
            if (fault) {
              const activity = fault.activities.find(a => a.uid === fa.Activity.UniqueId);
              if (activity && !activity.id && activity.id !== fa.Activity.Id) {
                activity.id = fa.Activity.Id;
              }
            }
          });
        }
      });

    // this.faultChangesCommitService.activityEditCommitted$
    //   .pipe(untilDestroyed(this))
    //   .subscribe(params => {
    //     // if (params.activityEdit.changes && params.activityEdit.activities) {
    //     //   params.activityEdit.activities.map(a => {
    //     //     const fault = this.loadedFaults.find(f => f.uid === a.fault.uid);
    //     //     if (fault) {
    //     //       const faultActivity = fault.activities.find(act => act.uid === a.uid);
    //     //       if(faultActivity) {
    //     //         Object.keys(params.activityEdit.changes).forEach(prp => {
    //     //           faultActivity.data[prp] = params.activityEdit.changes[prp];
    //     //         });
    //     //       }
    //     //     }
    //     //   });
    //     // }
    //   });
    //
    // this.groupSyncService.activityEditSynced$
    //   .pipe(untilDestroyed(this))
    //   .subscribe(activityEditData => {
    //     if (activityEditData.patchData && activityEditData.activities) {
    //       activityEditData.activities.map(a => {
    //         const fault = this.loadedFaults.find(f => f.uid === a.Fault.UniqueId);
    //         if (fault) {
    //           const faultActivity = fault.activities.find(act => act.uid === a.UniqueId);
    //           if(faultActivity) {
    //             Object.keys(activityEditData.patchData).forEach(prp => {
    //               if (faultActivity.data[prp] !== activityEditData.patchData[prp]) {
    //                 faultActivity.data[prp] = activityEditData.patchData[prp];
    //               }
    //             });
    //           }
    //         }
    //       });
    //     }
    //
    //     if (multiActivityData.FaultActivities) {
    //       multiActivityData.FaultActivities.map(async fa => {
    //         const fault = this.loadedFaults.find(f => f.uid === fa.Fault.UniqueId);
    //         if (fault) {
    //           const activity = fault.activities.find(a => a.uid === fa.Activity.UniqueId);
    //           if (activity && !activity.id && activity.id !== fa.Activity.Id) {
    //             activity.id = fa.Activity.Id;
    //           }
    //         }
    //       });
    //     }
    //
    //   });
  }

  public async refreshFaults(loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {
    if (!this.faultsLoadedGridNoFieldsCase) {
      return;
    }

    const filterState = await this.filterService.state.pipe(first()).toPromise();
    const layoutState = await this.layoutStateService.currentLayoutState$.pipe(first()).toPromise();
    await this.reloadAllFaults(filterState, layoutState.state, false, true, loadOptions);
  }
  public async reloadAllFaults(filterModel: MmFilterModel, layoutState: LayoutStateModel, reloadFaultDetails: boolean = false, refreshByTileView: boolean = false, loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {

    this.loadingSubject.next(true);
    try {
      this.onLayoutStateChanged.next(true);
      await this.loadAllFaultIds(this.project, this.filter.structure, this.layoutState, reloadFaultDetails, refreshByTileView, loadOptions);
      this.mmFaultsCountService.removeDeletedFaults(false);
      this.mmFaultsCountService.removeNewFaults(undefined, false);

      const faultCount = await firstValueFrom(this.faultDataService.getAllFaultCount(filterModel.structure.Id, layoutState.layout.Id, layoutState.layout.FilterDisabled));
      this.mmFaultsCountService.setAllFaultsCount(faultCount);

    } catch (err) {
      console.error(err);
      this.notificationService.error(await this.translateService.get('MM.fault.loadError').toPromise());
    }
    this.loadingSubject.next(false);
  }

  ngOnDestroy(): void {
    this.pendingService.removePendingState(this.loadingFaultsSubject);
    this.pendingService.removePendingState(this.loadingLayoutFaultsSubject);
  }

  public shouldReloadFaults(filterModel: MmFilterModel, layoutStateModel: LayoutStateModel): boolean {
    if (!filterModel?.project || !layoutStateModel?.project || filterModel.project.MyDocmaUid !== layoutStateModel.project.MyDocmaUid) {
      return false;
    }
    const isProjectStructureChanged = this.isProjectStructureChanged(filterModel);
    const isLayoutStateChanged = this.isLayoutStateChanged(layoutStateModel);
    const shouldReload = (isProjectStructureChanged || isLayoutStateChanged) && !!layoutStateModel?.layout?.Id;
    return shouldReload;
  }

  private async faultChanged(fault: Fault) {
    const found = this.loadedFaults.find(f => (fault.id && fault.id === f.id) || (fault.uid && fault.uid === f.uid));
    if (found) {
      /* currently nothing to do here... */
    } else {
      if (fault.changeState.status === ChangeStatusEnum.New) {
        if (fault instanceof ViewFault) {
          this.loadedFaults.push(fault as ViewFault);
          this.faultsSubject.value.loadedFaults.push(fault as ViewFault);
          if (this.hasFaultFilterTags(fault)) {
            this.onFaultAdded.emit(fault);
          }
        }
      }
    }
  }

  public async removeFaults(faults: Fault[], discardByUid: boolean = false) {
    const removedFaults: ViewFault[] = [];
    let removedNewFaults: number = 0;

    faults.forEach(fault => {
      let foundIndex = this.loadedFaults.findIndex(f => (fault.id && fault.id === f.id) || (fault.uid && fault.uid === f.uid));
      if (foundIndex > -1) {
        this.loadedFaults.splice(foundIndex, 1);
        let foundIndex2 = this.faultsSubject.value.loadedFaults.findIndex(f => (fault.id && fault.id === f.id) || (fault.uid && fault.uid === f.uid));
        if (foundIndex2 > -1) {
          removedFaults.push(this.faultsSubject.value.loadedFaults[foundIndex2]);

          if (!!this.faultsSubject.value[foundIndex2]?.unsavedNew) {
            removedNewFaults++;
          }
          this.faultsSubject.value.loadedFaults.splice(foundIndex2, 1);
        }
      }
    });

    if (removedFaults.length > 0) {

      if (discardByUid) {
        removedFaults.forEach(x => x.id = undefined);
      }

      this.onFaultsRemoved.emit(removedFaults);
      this.mmFaultsCountService.removeNewFaults(removedNewFaults);
    }
  }

  async addFault(fault: ViewFault) {
    const viewFault = fault;
    if (fault.data.Pin) {
      const pin: BpPin = {...fault.data.Pin, ...{ExternalId: fault.uid} };
      fault.pin = this.pinsLoaderService.convertToCoordinate(pin);
      fault.bpPin = pin;
      this.pinsLoaderService.addPin(pin);
    }

    this.loadedFaults.splice(0, 0, viewFault);
    this.faultsSubject.value.loadedFaults.splice(0, 0, viewFault);

    this.onFaultAdded.emit(viewFault);

    this.mmFaultsCountService.addNewFaults(1);
  }

  getFault(faultUid: string): ViewFault {
    return this.loadedFaults.find(f => f.uid === faultUid);
  }

  getFaults(faultUids: string[]): ViewFault[] {
    return this.loadedFaults.filter(f => faultUids.findIndex(x => x === f.uid) !== -1);
  }

  private onOfflineChanged(offline: boolean) {
    if (this.offline !== offline) {
      this.offline = offline;
      this.forceLayoutReload = true;
    }
  }

  private onProjectChanged() {
    this.project = null;
    this.filter = null;
    this.selectionService.stopSelectionMode();
    this.faultsSubject.next({loadedFaults: [], loadOptions: LoadLayoutFaultsOptions.None});
    return true;
  }

  private onCurrentProjectChanged(p: Project): boolean {
    this.project = p;
    // this.filter = null;
    // this.selectionService.stopSelectionMode();
    // this.faultsSubject.next([]);
    return true;
  }

  private isProjectStructureChanged(f: MmFilterModel): boolean {

    if (!this.project || !f || !f.project || f.project.MyDocmaUid !== this.project.MyDocmaUid) {
      return false;
    }
    if (isIdsEquals(this.filter && this.filter.structure, f.structure)) {
      return false;
    }
    this.filter = f;

    return true;
  }

  private isLayoutStateChanged(ls: LayoutStateModel): boolean {

    if (this.forceLayoutReload) {
      this.forceLayoutReload = false;
      return true;
    } else {
      if (!this.project || !ls || !ls.layout || !ls.project || ls.project.MyDocmaUid !== this.project.MyDocmaUid) {
        return false;
      }
      let isLayoutChanged = !isIdsEquals(this.layoutState && this.layoutState.layout, ls.layout);
      if (isLayoutChanged) {
        this.selectionService.stopSelectionMode();
      } else {
        // TODO: Check Filters/Layouts
        // isLayoutChanged = this.layoutState?.filters?.length > 0 || ls?.filters?.length > 0 ||
        // this.layoutState?.sortItems?.length > 0 || ls?.sortItems?.length > 0;
        isLayoutChanged = this.layoutState.id !== ls.id;
      }

      this.layoutState = ls;
      return isLayoutChanged;
    }
  }

  private reset() {
    this.faultsSubject.next({loadedFaults: [], loadOptions: LoadLayoutFaultsOptions.None});
  }

  private async getApiSearchParams(structureId: number, layoutState: LayoutStateModel, onlySearchIds: boolean, setSorting: boolean = false, setFilter: boolean = true) {
    const fieldCriteriaFormats: {[fieldFormat: string]: any} = {};
    fieldCriteriaFormats['CreatedOnFieldFormat'] = CreatedOnFieldFormat.Date;
    fieldCriteriaFormats['ModifiedOnFieldFormat'] = ModifiedOnFieldFormat.Date;
    fieldCriteriaFormats['CreatedOnMobileFieldFormat'] = CreatedOnFieldFormat.Date;
    fieldCriteriaFormats['ModifiedOnMobileFieldFormat'] = ModifiedOnFieldFormat.Date;

    const searchParams = new FaultDataSearch({
      Structure: structureId.toString(),
      LayoutId: layoutState.layout.Id,
      OnlyDefectId: true,
      FieldCriteriaFormats: fieldCriteriaFormats
    });

    if (layoutState.layout.FilterDisabled !== undefined) {
      searchParams.IsLayoutFilterDisabled = layoutState.layout.FilterDisabled;
    }

    if (setFilter) {
      const searchFilter = new FaultDataSearchFilter();
      let layoutFilterCriterias: FilterCriteria[] = [];

      if (layoutState.filters && layoutState.filters.length > 0) {
        layoutFilterCriterias = layoutState.filters.map(f => new FilterCriteria(f));
      }

      let parentId = layoutFilterCriterias.find(x => !x.ParentId)?.Id;
      let filterId = Math.max(...layoutFilterCriterias.map(x => x.Id));
      if (!parentId && filterId === -Infinity) {
        filterId = 0;
      }

      searchFilter.FilterItems = [{ Criterias: [...layoutFilterCriterias, ...(layoutState.searchFilters ?? [])] }];

      searchParams.Filter = searchFilter;
    }

    if (!setSorting) {
      searchParams.SortingSource = FaultSortingSourceEnum.None;
    } else {
      const sortItemsParam = await this.colDefsService.getSortItems();
      layoutState.sortItems = sortItemsParam.LayoutSortItems;

      if (layoutState.sortItems && sortItemsParam.SortingSource === FaultSortingSourceEnum.Passed) {    //layoutState.sortItems.length > 0
        searchParams.SortingSource = sortItemsParam.SortingSource;
        searchParams.Sorting = layoutState.sortItems.map(m => ({DefaultName: m.Field.DefaultName, SortDirection: m.Direction, IsGroupSorting: m.IsGroupSorting  }));
        // console.warn('<<< searchParams >>>');
        // console.warn(searchParams);

      } else {
        searchParams.SortingSource = FaultSortingSourceEnum.Layout;
      }
    }
    searchParams.AdditionalData = (onlySearchIds ? ['Defect_UniqueId', 'Defect_AttributeId', 'PlanPinText'] : ['Defect_UniqueId', 'Defect_AttributeId']);
    return searchParams;
  }

  onFaultRefreshed = new Subject<ViewFault>();

  private laodViewFaultPin(vf: ViewFault) {
    const lf = new LayoutFault({id: vf.id, uid: vf.uid, isLocked: vf.isLocked});
    lf.data.Coordinates = [{FileId: vf.data.PlanId, PosX: vf.data.PlanPositionX, PosY: vf.data.PlanPositionY}];
    this.loadFaultPin(vf, lf);
  }
  private loadFaultPin(vf: ViewFault,lf: LayoutFault) {
    const bpPin = this.pinsLoaderService.getPinByFaultUid(lf.data.UniqueId);
    if (bpPin) {
      vf.pin = this.pinsLoaderService.convertToCoordinate(bpPin);
      vf.bpPin = bpPin;
    } else {
      vf.pin = lf.data?.Coordinates?.length > 0 ? lf.data?.Coordinates[0] : null;
    }
  }

  private async loadAllFaultIds(project: Project, structure: Structure, layoutState: LayoutStateModel, reloadFaultDetails: boolean = false, refreshByTileView: boolean = false, loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {

    const structureId = structure && structure.Id ? structure.Id : project.Structure.Id;
    this.faultsLoadedGridNoFieldsCase = (layoutState.layout.Fields.length === 0 && this.toggledLeftViews.includes(HeaderToggleMenuEnum.gridView));


    this.loadedFaults = [];

    const selecetdFaults = await this.selectionService.selectedItems.pipe(first()).toPromise();

    let offlineData = await this.faultService.getOfflineFaults(project.MyDocmaUid);
    if (this.offline) {
      if (structure && structure.Id !== project.Structure.Id) {
        this.projectStructures = (await this.projectLoaderService.structures.pipe(filter(s => s.loadState.status === LoadStatusEnum.Loaded), first()).toPromise()).data;
        const loadedStructure = this.projectStructures[0].findById(structure.Id);
        offlineData = offlineData.filter(f => f.loadState.status === LoadStatusEnum.Error ||
          (f.data.Structure && f.data.Structure.Id && loadedStructure.findById(f.data.Structure.Id)));
      }
      this.loadedFaults = offlineData.map(f => {
        const vf = new ViewFault(f);
        vf.project = project;
        this.applySelectedState(vf, selecetdFaults);
        this.laodViewFaultPin(vf);
        //    vf.pin = f.data?.Coordinates?.length > 0 ? f.data?.Coordinates[0] : null;
        return vf;
      });

      if (this.currentFault && this.currentFault?.unsavedNew && this.loadedFaults.findIndex(x => x.uid === this.currentFault.uid) === -1) {
        // console.warn('0#ADDED CURRENT_FAUTLT AGAIN!!');
        // console.warn(this.currentFault);
        this.loadedFaults.splice(0 , 0, this.currentFault);
      }

      this.filterFaultsByTags(loadOptions);

      return;
    }

    const searchParams = await this.getApiSearchParams(structureId, this.layoutState, true, true);

    const isTileView = (this.appContainerService.isMobileView ? true : (await this.personalizationService.getValue(PersonalizationKeysEnum.Fault_LeftToggleGroup))?.some(x => x === 'tileView'));

    const data = await iif(() => (layoutState.layout.Fields.length > 0 || isTileView || refreshByTileView),
      this.faultDataService.searchFaults(searchParams).pipe(takeUntil(this.onLayoutStateChanged)),
      of<LayoutFault[]>([])
    ).toPromise();

    const newOfflineData = [];
    const otherOfflineData = [];
    offlineData.forEach(o => {
      if (o.changeState && o.isNew) {
        newOfflineData.push(o);
      } else {
        otherOfflineData.push(o);
      }
    });
    if (otherOfflineData.length) {
      this.loadedFaults = data?.map(f => {
        const foundOffline = offlineData.find(o => o.id === f.id);
        let vf: ViewFault;
        if (foundOffline) {
          vf = new ViewFault(foundOffline);
          vf.project = project;
          vf.layoutState = f;
          vf.pinText = f.data?.PlanPinText;
          vf.attributeId = f.data?.Attribute?.id;
          vf.isLocked = f.isLocked;
          if (vf.data) {
            vf.data.IsLocked = f.isLocked;
          }
          this.loadFaultPin(vf, f);
          this.laodViewFaultPin(vf);
          if (reloadFaultDetails) {
            this.onFaultRefreshed.next(vf);
          }
          // if (vf.data) {
          //   vf.data.Attribute = f.;
          // }
        } else {
          vf = new ViewFault({ project, id: f.id, uid: f.uid, layoutState: f, isLocked: f.isLocked, attributeId: f.data?.attributeId, pinText: f.data?.PlanPinText });
          this.loadFaultPin(vf,f);
        }
        this.applySelectedState(vf, selecetdFaults);
        return vf;
      });
    } else {
      this.loadedFaults = data?.map(f => {
        const vf = new ViewFault({
          project,
          id: f.id, uid: f.uid, layoutState: f,
          pinText: f.data?.PlanPinText,
          attributeId: f.data?.AttributeId
        });
        this.loadFaultPin(vf,f);
        this.applySelectedState(vf, selecetdFaults);
        return vf;
      });
    }
    if (newOfflineData.length > 0) {
      this.loadedFaults.unshift(...newOfflineData.map(f => {
        const vf = new ViewFault(f);
        vf.project = project;
        this.applySelectedState(vf, selecetdFaults);
        vf.layoutState = new LayoutFault({ loadState: new LoadState({ status: LoadStatusEnum.Loaded, data: {} }) });
        vf.pin = f.data.pin;
        vf.attributeId = f.data?.AttributeId;
        vf.pinText =  f.data?.PlanPinText,
          vf.isLocked = f.isLocked;
        if (vf.data) {
          vf.data.IsLocked = f.isLocked;
        }
        this.laodViewFaultPin(f);
        return vf;
      }));
    }

    if (this.currentFault && this.currentFault?.unsavedNew && this.loadedFaults.findIndex(x => x.uid === this.currentFault.uid) === -1) {
      // console.warn('1#ADDED CURRENT_FAUTLT AGAIN!!');
      // console.warn(this.currentFault);
      this.loadedFaults.splice(0 , 0, this._currentFault);
    }

    await this.filterFaultsByTags(loadOptions);
  }

  private applySelectedState(vf: ViewFault, selectedFaults: ViewFault[] ) {
    const foundIndex =  selectedFaults.findIndex(s => this.selectionService.compareItems(vf, s));
    if (foundIndex > -1 && selectedFaults[foundIndex] !== vf ) {
      selectedFaults[foundIndex] = vf;
    }
    vf.isChecked = foundIndex > -1;
  }

  private hasFaultFilterTags(f: Fault): boolean {
    const tagNew = this.filter.getTag('fault-new');
    const tagChanged = this.filter.getTag('fault-changed');
    const tagDownloaded = this.filter.getTag('fault-downloaded');
    const tagSyncError = this.filter.getTag('fault-sync-error');
    const tagLoadError = this.filter.getTag('fault-load-error');
    let hasTags = true;
    if (tagNew) {
      hasTags = f.changeState && f.changeState.status === ChangeStatusEnum.New;
    }
    if (tagChanged && hasTags) {
      hasTags = f.changeState && f.changeState.status === ChangeStatusEnum.Changed;
    }
    if (tagDownloaded && hasTags) {
      hasTags = f.downloaded;
    }
    if (tagSyncError && hasTags) {
      hasTags = f.syncState && f.syncState.status === SyncStatusEnum.Error;
    }
    if (tagLoadError && hasTags) {
      hasTags = f.loadState && f.loadState.status === LoadStatusEnum.Error;
    }
    return hasTags;
  }


  private async filterFaultsByTags(loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {
    const tagNew = this.filter.getTag('fault-new');
    const tagChanged = this.filter.getTag('fault-changed');
    const tagDownloaded = this.filter.getTag('fault-downloaded');
    const tagSyncError = this.filter.getTag('fault-sync-error');
    const tagLoadError = this.filter.getTag('fault-load-error');
    let filteredFaults = (this.loadedFaults && this.loadedFaults.length > 0 ? [...this.loadedFaults] : []);
    if (tagNew) {
      filteredFaults = filteredFaults.filter(f => f.changeState && f.changeState.status === ChangeStatusEnum.New);
    }
    if (tagChanged) {
      filteredFaults = filteredFaults.filter(f => f.changeState && f.changeState.status === ChangeStatusEnum.Changed);
    }
    if (tagDownloaded) {
      filteredFaults = filteredFaults.filter(f => f.downloaded);
    }
    if (tagSyncError) {
      filteredFaults = filteredFaults.filter(f => f.syncState && f.syncState.status === SyncStatusEnum.Error);
    }
    if (tagLoadError) {
      filteredFaults = filteredFaults.filter(f => f.loadState && f.loadState.status === LoadStatusEnum.Error);
    }

    if (this.syncStateChanges) {
      await Promise.all(Object.keys(this.syncStateChanges).map(async f => {
        const fault = filteredFaults.find(x => x.uid === f);
        if (fault && this.syncStateChanges[f].syncState?.status && fault.syncState.status !== this.syncStateChanges[f].syncState?.status) {
          fault.syncState.status = this.syncStateChanges[f].syncState.status;
        }
      }));
    }

    this.faultsSubject.next({loadedFaults: filteredFaults, loadOptions});

  }

  public loadAttachmentSource(fault: ViewFault, attachment: Attachment | number): Observable<AttachmentSource> {
    if (!fault) {
      throw new Error('Fault is not set');
    }
    let found: Attachment;
    if (typeof attachment === 'number') {
      const attachmentId = attachment;
      if (fault && fault.attachments) {
        found = fault.attachments.find(a => a.id === attachmentId);
      }
      if (!found) {
        fault.attachments = fault.attachments || [];
        found = new Attachment();
        found.id = attachmentId;
        fault.attachments.push(found);
      }
    } else {
      found = attachment;
    }
    let loadingType = AttachmentLoadingTypeEnum.FromServer;
    if (fault.downloaded) {
      loadingType = AttachmentLoadingTypeEnum.FromDb;
    } else if (this.offline) {
      loadingType = found.changeState.status === ChangeStatusEnum.New ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.None;
    } else {
      loadingType = found.changeState.status === ChangeStatusEnum.New ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.FromServer;
    }

    return from(this.attachmentService.getAttachmentSource(found));
  }

  public async loadAttachmentThumbnail(fault: ViewFault, attachment: Attachment | number, size?: { height: number, width: number }) {
    if (!fault) {
      throw new Error('Fault is not set');
    }

    let found: Attachment;
    if (typeof attachment === 'number') {
      const attachmentId = attachment;
      if (fault && fault.attachments) {
        found = fault.attachments.find(a => a.id === attachmentId);
      }
      if (!found) {
        fault.attachments = fault.attachments || [];
        found = new Attachment();
        found.id = attachmentId;
        fault.attachments.push(found);
      }
    } else {
      found = attachment;
    }
    let loadingType = AttachmentLoadingTypeEnum.FromServer;
    if (fault.downloaded) {
      loadingType = AttachmentLoadingTypeEnum.FromDb;
    } else if (this.offline) {
      loadingType = found.isNew ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.None;
    } else {
      loadingType = found.isNew ? AttachmentLoadingTypeEnum.FromDb : AttachmentLoadingTypeEnum.FromServer;
    }

    return this.attachmentService.loadAttachmentThumbnail(fault.project.MyDocmaUid, found, size, loadingType);
  }

  public async loadLayoutFaults(faultsToLoad: ViewFault[], force: boolean = false, notifyNotInFilterMessage: boolean = false, loadOptions: LoadLayoutFaultsOptions = LoadLayoutFaultsOptions.None) {

    if (!faultsToLoad || faultsToLoad.length === 0) {
      this.loadLayoutFaultsOptionsSubject.next(loadOptions);
      return of([]);
    }
    const structureId = ((this.filter.structure && this.filter.structure.Id) ? this.filter.structure.Id : this.filter.project.Structure.Id);
    const layoutId = ((this.layoutState.layout && this.layoutState.layout.Id) ? this.layoutState.layout.Id : 0);

    const faultsById: { [id: number]: string } = Object.fromEntries(faultsToLoad.map(f => [f.id, f.uid]));

    let faultIds: number[] = [];
    if (!force) {
      faultsToLoad.forEach(vf => {
        if (vf.layoutState.loadState.status !== LoadStatusEnum.Loaded &&
          vf.layoutState.loadState.status !== LoadStatusEnum.Loading) {
          if (!this.connectivityService.isOfflineVal) {
            vf.layoutState.loadState.status = LoadStatusEnum.Loading;
          }
          if (!vf.isNew) {
            faultIds.push(vf.id);
          }
        }
      });
    } else {
      faultIds = faultsToLoad.map(f => f.id);
    }

    const searchParams = await this.getApiSearchParams(structureId, this.layoutState, false, false, force);

    searchParams.Ids = faultIds;
    if (faultIds.length !== 0) {
      return of({
        layoutStateId: this.layoutState.id,
        faultIds: [...faultIds],
        params: searchParams,
        layoutFieldsCount: this.layoutState.layout.Fields.length
      }).pipe(
        tap(x => {
          this.loadingLayoutFaultsSubject.next(true);
        }),
        switchMap(request => this.faultDataService.loadLayoutFaults(request.params).pipe(map(response => ({ request, response })))),
        takeUntil(this.onLayoutStateChanged),
        map(r => {
          const data = r.response;
          const faultsToUpdate = [];
          const faultsToDelete = [];

          if (data && r.request.layoutStateId === this.layoutState.id) {
            let faultsNotInFilter = false;

            // if (r.request.faultIds.length > 1) {
            //   console.warn(r.request.faultIds.length);
            // }

            r.request.faultIds.forEach(id => {
              const result = data.find(f => f.id === id);


              if (result && (r.request.layoutFieldsCount > 0 || this.toggledLeftViews?.includes(HeaderToggleMenuEnum.tileView))) {
                faultsToUpdate.push(this.refreshViewFaultData(result, !force));
              } else {
                // fault not found in layout
                this.selectionService.stopSelectionMode();
                faultsToDelete.push(new Fault({ uid: faultsById[id] }));  // discard faults in grid by uid
                faultsNotInFilter = true;
              }
            });

            if (notifyNotInFilterMessage && faultsNotInFilter) {
              this.mmFaultsCountService.removeNewFaults(1);
              const messageKey = (r.request.layoutFieldsCount > 0 ? 'MM.fault.notInFilter' : 'MM.fault.notInLayout');
              this.translateService.get(messageKey).subscribe(t => this.notificationService.info(t));
            }

            if (faultsToDelete.length > 0) {
              this.removeFaults(faultsToDelete, true);
            }
            // this.faultService.validateChangeState(this.project.MyDocmaUid, null, faultsToUpdate);

            this.layoutFaultsSubject.next(faultsToUpdate);
            this.loadLayoutFaultsOptionsSubject.next(loadOptions);

            return faultsToUpdate;
          } else {
            return [];
          }
        }),
        finalize(() => this.loadingLayoutFaultsSubject.next(false))
      );
    } else {
      this.loadLayoutFaultsOptionsSubject.next(loadOptions);
      return of([]);
    }
  }

  public loadFaults(faultsToLoad: ViewFault[], force: boolean = false): Observable<ViewFault[]> {
    if (this.offline) {
      return of(faultsToLoad);
    }
    this.loadingFaultsSubject.next(true);

    let structureId = -1;
    this.viewService.currentProject$.pipe(tap(x => structureId = x?.Structure?.Id)).toPromise();

    return from(this.faultService.loadFaults(this.filter.project.MyDocmaUid, structureId, faultsToLoad, force))
      .pipe(
        map(faults => faults as ViewFault[]),
        finalize(() => this.loadingFaultsSubject.next(false))
      );
  }

  private refreshViewFaultData(layoutFault?: LayoutFault, markFaultAsLoaded = true): ViewFault {

    if (!layoutFault) {
      return null;
    }
    const faultId = (layoutFault && layoutFault.id);
    if (!faultId) {
      return null;
    }
    const found = this.faultsSubject.value.loadedFaults.find(f => {
      return f.id === layoutFault.id || f.uid === layoutFault.uid;
    });

    if (found) {
      if (layoutFault) {
        found.firstLoad = found.layoutState.loadState.status !== LoadStatusEnum.Loaded;
        found.layoutState.loadState = new LoadState({ data: layoutFault.data, status: LoadStatusEnum.Loaded });
        if (!markFaultAsLoaded) {
          found.firstLoad = false;
        }
        if (!found.uid && found.layoutState.uid) {
          found.uid = found.layoutState.uid;
        }
        this.layoutFaultSubject.next(found);
      }
      return found;
    } else {
      return null;
    }
  }

  private async removeGroupFaultActivites(activity: Activity, groupDelete: boolean, modifiedOn: Date, modifier: User) {
    await Promise.all(this.loadedFaults.map(async fault => {
      let changed = false;

      const activities = fault.activities
        .filter(act => (groupDelete && act?.data?.Group?.UniqueId === activity.group.uid) || (!groupDelete && act?.data.Id === activity.id));

      const updFaultsCountActivities = fault.activities
        .filter(act => (!groupDelete && act?.data.Id !== activity.id && act?.data?.Group?.UniqueId === activity.group?.uid));

      await Promise.all(activities.map(async act => {
        fault.removeActivity(act);
        changed = true;
      }));
      await Promise.all(updFaultsCountActivities.map(async act => {
        act.data.FaultsCount--;
        changed = true;
      }));

      if (changed) {
        fault.data.ModifiedOn = modifiedOn;
        fault.data.Modifier = modifier;
        fault.loadState.data.ModifiedOn = modifiedOn;
        fault.loadState.data.Modifier = modifier;
        if ((activities?.length ?? 0) > 0) {
          this.activitiesDeletedSubject.next(activities);
        }
      }
    }));
  }

  async discardLoadedFaults(faultUids: string[]) {
    this.loadedFaults.filter(x => faultUids.findIndex(f => x.uid === f) !== -1).forEach(fault => {
      console.warn('discard load state of loaded-fault - id: ' + fault.id + ', uid: ' + fault.uid);
      fault.loadState = new LoadState({status: LoadStatusEnum.NotLoaded});
    });
  }

  public async ChangeActivityAttachment(attachment: Attachment, activity: Activity) {
    Promise.all(this.loadedFaults.map(async f => {
      if (activity?.fault && f.uid !== activity.fault.uid) {
        f.activities.forEach(act => {
          if (act?.group && act.group?.uid === activity.group?.uid && act.uid !== activity.uid) {
            const foundAtt = act.attachments.find(at => at.uid === attachment.uid);
            if (!foundAtt) {
              act.attachments.push(new Attachment(attachment));
            } else {
              Object.assign(foundAtt.data, attachment.changeState.data);
            }
          }
        });
      }
    }));
  }

  public async AddActivityAttachments(attachments: Attachment[], activity: Activity){
    Promise.all(this.loadedFaults.map(async f => {
      if (activity?.fault && f.uid !== activity.fault.uid) {
        f.activities.forEach(act => {
          if (act?.group && act.group?.uid === activity.group?.uid && act.uid !== activity.uid) {
            const newAttachments = attachments.filter(at => act.attachments.findIndex(f => f.uid === at.uid) === -1);
            if (newAttachments) {
              newAttachments.forEach(att => {
                act.attachments.push(att);
              });
            }
          }
        });
      }
    }));
  }

  public async DeleteActivityAttachment(attachment: Attachment, activity: Activity, modifiedOn: Date, modifier: User) {
    Promise.all(this.loadedFaults.map(async f=> {
      let changed = false;
      if (activity?.fault && f.uid !== activity.fault.uid) {
        f.activities.forEach(act => {
          if (act?.group && act.group?.uid === activity.group?.uid && act.uid !== activity.uid) {
            const foundAtt = act.attachments.findIndex(at => at.uid === attachment.uid);
            if (foundAtt !== -1) {
              act.attachments.splice(foundAtt, 1);
              changed = true;
            }
          }
        });
      }

      if (changed) {
        f.data.ModifiedOn = modifiedOn;
        f.data.Modifier = modifier;
        f.loadState.data.ModifiedOn = modifiedOn;
        f.loadState.data.Modifier = modifier;
      }
    }));
  }

  public async ApplyMultiActivityChanges(param: {multiActivity: MultiActivity, changes: ActivityData, patchMode: ActivityPatchModeEnum}) {
    if (param.multiActivity.entities) {

      const entities = (param.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? param.multiActivity.entities.filter(e => e.IsInitActivity) : param.multiActivity.entities);
      const groupEntities = (param.patchMode === ActivityPatchModeEnum.MultiActivity_Single ? param.multiActivity.entities.filter(e => !e.IsInitActivity) : []);

      entities.map(async e => {
        const fault = this.loadedFaults.find(f => f.uid === e.Fault.uid);
        if (fault) {
          const activity = fault.activities.find(a => a.uid === e.Activity.uid);
          if (activity) {
            if (!activity.id && activity.id !== e.Activity.id) {
              activity.id = e.Activity.id;
            }
            Object.keys(param.changes).forEach(key => {
              if (activity.data[key] !== param.changes[key]){
                // activity.data[key] = param.changes[key];
              }
            });
          }
          else {
            fault.activities.push(e.Activity);
          }
        }
      });

      groupEntities.map(async e => {
        const fault = this.loadedFaults.find(f => f.uid === e.Fault.uid);
        if (fault) {
          const activity = fault.activities.find(a => a.uid === e.Activity.uid);
          if (activity) {
            activity.data.FaultsCount--;
          }
        }
      });

    }
  }

}
